import React, { useState, useEffect, useMemo } from 'react';
import { Search, Calendar, FileDown, Printer, ArrowLeft, ArrowRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/UI/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/UI/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/UI/dialog";
import { Button } from "@/components/UI/button";
import { Input } from "@/components/UI/input";
import { Alert, AlertDescription } from "@/components/UI/alert";
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {FileCheck} from 'lucide-react';
import { supabase } from '@/services/supabaseClient';
import { uploadInvoicePDF } from '@/services/supabaseStorage';
// Utility Functions
const formatCurrency = (amount) => {
  if (!amount) return '0.00';
  return Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const extractProjectName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.split('-');
  if (parts.length >= 3) {
    return parts[1].trim(); // Extract middle part
  }
  return fullName;
};

const formatMonth = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
};


const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long'
  });
};

const formatShortDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).toUpperCase();
};

const formatTaxInvoiceRef = (po, location) => {
  // Extract 4 digits from PO number
  const match = po.match(/\d{4}/);
  const middleDigits = match ? match[0] : '0000';
  return `LOAMS-${middleDigits}-${location}`;
};
const formatContractPeriod = (startDate, endDate) => {
  if (!startDate || !endDate) return '-';
  const start = new Date(startDate);
  const end = new Date(endDate);
  const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + 
                    (end.getMonth() - start.getMonth()) + 1;

  // Format day to ensure two digits
  const startDay = start.getDate().toString().padStart(2, '0');
  const endDay = end.getDate().toString().padStart(2, '0');

  // Get month in short format and uppercase
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();

  // Get full year
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear} (${monthsDiff} months)`;
};



// Add this helper function to convert PDF to base64
const convertPdfToBase64 = async (pdfBlob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(pdfBlob);
  });
};

const generateLetterheadInvoice = async (invoice) => {
  try {
    const element = document.getElementById(`invoice-preview-${invoice.invoice_no}`);
    if (!element) {
      throw new Error('Invoice preview element not found');
    }

    // A4 dimensions in mm
    const A4_WIDTH_MM = 210.06;
    const A4_HEIGHT_MM = 296.93;

    // Create container with exact A4 dimensions
    const container = document.createElement('div');
    Object.assign(container.style, {
      position: 'relative',
      width: `${A4_WIDTH_MM}mm`,
      height: `${A4_HEIGHT_MM}mm`,
      margin: '0',
      padding: '0',
      overflow: 'hidden',
      backgroundColor: 'white'
    });

    // Add letterhead background with full coverage
    const letterheadDiv = document.createElement('div');
    Object.assign(letterheadDiv.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundImage: 'url("/LetterHead_Sign.jpg")',
      backgroundSize: '100% 100%',
      backgroundPosition: 'top left',
      backgroundRepeat: 'no-repeat',
      zIndex: '1'
    });

    // Style the invoice content
    const content = element.cloneNode(true);
    Object.assign(content.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      padding: '20mm',
      boxSizing: 'border-box',
      backgroundColor: 'transparent',
      zIndex: '2'
    });

    // Assemble the document
    container.appendChild(letterheadDiv);
    container.appendChild(content);
    document.body.appendChild(container);

    // PDF generation options
    const opt = {
      margin: 0,
      filename: `LOAMS-${invoice.invoice_no}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: {
        scale: 4, // Increased for better quality
        useCORS: true,
        letterRendering: true,
        width: A4_WIDTH_MM * 3.78, // Convert mm to pixels (96 DPI)
        height: A4_HEIGHT_MM * 3.78,
        backgroundColor: null,
        logging: true,
        removeContainer: true,
        imageTimeout: 0,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector(`#invoice-preview-${invoice.invoice_no}`);
          if (clonedElement) {
            clonedElement.style.transform = 'none';
            clonedElement.style.zoom = '1';
          }
        }
      },
      jsPDF: {
        unit: 'mm',
        format: [A4_WIDTH_MM, A4_HEIGHT_MM],
        orientation: 'portrait',
        compress: true,
        precision: 16,
        putTotalPages: true
      }
    };

    try {
      // Generate PDF
      await html2pdf()
        .set(opt)
        .from(container)
        .save();
    } finally {
      // Cleanup
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }

  } catch (error) {
    console.error('Error generating letterhead invoice:', error);
    throw error;
  }
};


// First add the component definition
const InvoicePreviewActions = ({ invoice, onDownload }) => {
  return (
    <div className="flex items-center gap-2">
      {invoice.public_url && (
        <a 
          href={invoice.public_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 flex items-center"
        >
          <FileDown className="h-4 w-4 mr-1" />
          View Stored Invoice
        </a>
      )}
      {invoice.certificate_url && (
        <a 
          href={invoice.certificate_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-green-600 hover:text-green-800 flex items-center"
        >
          <FileCheck className="h-4 w-4 mr-1" />
          View Certificate
        </a>
      )}
      <Button onClick={() => onDownload(invoice)}>
        <Printer className="h-4 w-4 mr-2" />
        Generate New
      </Button>
    </div>
  );
};







const NumberToWords = {
  ones: {
    0: '', 1: 'One', 2: 'Two', 3: 'Three', 4: 'Four',
    5: 'Five', 6: 'Six', 7: 'Seven', 8: 'Eight', 9: 'Nine',
    10: 'Ten', 11: 'Eleven', 12: 'Twelve', 13: 'Thirteen', 14: 'Fourteen',
    15: 'Fifteen', 16: 'Sixteen', 17: 'Seventeen', 18: 'Eighteen', 19: 'Nineteen'
  },
  tens: {
    2: 'Twenty', 3: 'Thirty', 4: 'Forty', 5: 'Fifty',
    6: 'Sixty', 7: 'Seventy', 8: 'Eighty', 9: 'Ninety'
  },

  handleTwoDigits(n) {
    if (n === 0) return '';
    if (n <= 19) return this.ones[n];
    
    const tensDigit = Math.floor(n / 10);
    const onesDigit = n % 10;
    if (onesDigit === 0) return this.tens[tensDigit];
    return `${this.tens[tensDigit]} ${this.ones[onesDigit]}`;
  },

  handleThreeDigits(n) {
    if (n === 0) return '';
    if (n <= 99) return this.handleTwoDigits(n);
    
    const hundreds = Math.floor(n / 100);
    const rest = n % 100;
    if (rest === 0) return `${this.ones[hundreds]} Hundred`;
    return `${this.ones[hundreds]} Hundred and ${this.handleTwoDigits(rest)}`;
  },

  convertWholeNumber(num) {
    if (num === 0) return 'Zero';

    const parts = [];
    
    // Handle billions
    const billions = Math.floor(num / 1000000000);
    if (billions > 0) {
      parts.push(`${this.handleThreeDigits(billions)} Billion`);
      num %= 1000000000;
    }

    // Handle millions
    const millions = Math.floor(num / 1000000);
    if (millions > 0) {
      parts.push(`${this.handleThreeDigits(millions)} Million`);
      num %= 1000000;
    }

    // Handle thousands
    const thousands = Math.floor(num / 1000);
    if (thousands > 0) {
      parts.push(`${this.handleThreeDigits(thousands)} Thousand`);
      num %= 1000;
    }

    // Handle last three digits
    if (num > 0) {
      parts.push(this.handleThreeDigits(num));
    }

    return parts.filter(Boolean).join(' ');
  },

  convert(amount) {
    try {
      // Handle string input with commas
      if (typeof amount === 'string') {
        amount = parseFloat(amount.replace(/,/g, ''));
      } else {
        amount = parseFloat(amount);
      }

      if (isNaN(amount)) return 'Zero Only';

      // Split into whole and decimal parts
      const whole = Math.floor(amount);
      const decimal = Math.round((amount - whole) * 100);

      if (whole === 0 && decimal === 0) return 'Zero Only';

      const result = [];
      
      if (whole > 0) {
        result.push(this.convertWholeNumber(whole));
      }

      if (decimal > 0) {
        result.push(`and ${decimal.toString().padStart(2, '0')}/100`);
      }

      result.push('Only');
      return result.join(' ');
    } catch (e) {
      console.error('Error in convert:', e);
      return 'Zero Only';
    }
  }
};

// Update the getAmountInWords function to use the new converter
const getAmountInWords = (amount) => {
  return NumberToWords.convert(amount);
};

const InvoicePreview = ({ invoice }) => {
  if (!invoice) return null;

  return (
    <div 
      id={`invoice-preview-${invoice.invoice_no}`} 
      className="font-sans"
      style={{
        width: '210.06mm',
        height: '296.93mm', // Updated height
        padding: '20mm',
        backgroundColor: 'white',
        position: 'relative',
        margin: '0 auto',
        boxSizing: 'border-box',
        fontSize: '11px',
        lineHeight: '1.5'
      }}
    >
      {/* TAX INVOICE Header */}
      <div className="mb-4">
        <div style={{ color: '#000088', fontSize: '16px', fontWeight: 'bold' }}>
          TAX INVOICE
        </div>
      </div>

      {/* Invoice Details */}
      <div className="space-y-1">
        <div className="flex">
          <div style={{ width: '100px' }}>S.N.</div>
          <div>: {invoice.invoice_no}</div>
        </div>
        <div className="flex">
          <div style={{ width: '100px' }}>Tax Invoice Ref.</div>
          <div>: {formatTaxInvoiceRef(invoice.po, invoice.location)}</div>
        </div>
        <div className="flex">
          <div style={{ width: '100px' }}>Tax Invoice Date</div>
          <div>: {formatShortDate(invoice.invoiceDate)}</div>
        </div>
      </div>

      {/* From Section with proper alignment */}
<div className="mt-4 mb-2">
  <div className="font-bold mb-1">From:</div>
  <div style={{ marginLeft: '0' }}>
    <div className="font-bold">Phoenician Technical Services LLC</div>
    <div style={{ marginTop: '2px' }}>
      P.O.Box: 377972, Dubai, UAE<br />
      TRN: 100034783900003
    </div>
  </div>
</div>

{/* To Section with proper alignment */}
<div className="mb-4">
  <div className="font-bold mb-1">To:</div>
  <div style={{ marginLeft: '0' }}>
    <div className="font-bold">Luxury Owner Association Management Services Co.LLC</div>
    <div style={{ marginTop: '2px' }}>
      P.O.Box: 2195, Dubai, UAE<br />
      TRN: 100567132400003
    </div>
  </div>
</div>

      {/* Project Details */}
      <div className="space-y-1 mb-4">
        <div className="flex">
          <div style={{ width: '100px' }}>Project</div>
          <div className="font-bold">: {extractProjectName(invoice.project_name)}</div>
        </div>
        <div className="flex">
          <div style={{ width: '100px' }}>Agreement Ref. No.</div>
          <div className="font-bold">: {invoice.po}</div>
        </div>
        <div className="flex">
          <div style={{ width: '100px' }}>Type of Service</div>
          <div className="font-bold">: {invoice.type_of_service}</div>
        </div>
        <div className="flex">
          <div style={{ width: '100px' }}>Contract Period</div>
          <div className="font-bold">: {formatContractPeriod(invoice.contract_start_date, invoice.contract_end_date)}</div>
        </div>
        <div className="flex">
          <div style={{ width: '100px' }}>Contract Value</div>
          <div className="font-bold">: AED {formatCurrency(invoice.contract_value)}</div>
        </div>
        <div className="flex">
          <div style={{ width: '100px' }}>Payment Terms</div>
          <div className="font-bold">: 30 Days</div>
        </div>
      </div>

      {/* Invoice Table */}
      <div style={{ marginBottom: '10px' }}>
        <table className="w-full border-collapse" style={{ borderColor: '#000' }}>
          <thead>
            <tr style={{ backgroundColor: '#000088', color: 'white' }}>
              <th className="border border-gray-400 p-2 text-center" style={{ width: '40px' }}>SN</th>
              <th className="border border-gray-400 p-2 text-left">Description</th>
              <th className="border border-gray-400 p-2 text-center" style={{ width: '60px' }}>QTY</th>
              <th className="border border-gray-400 p-2 text-center" style={{ width: '80px' }}>Unit Price</th>
              <th className="border border-gray-400 p-2 text-center" style={{ width: '80px' }}>Taxable</th>
              <th className="border border-gray-400 p-2 text-center" style={{ width: '60px' }}>VAT %</th>
              <th className="border border-gray-400 p-2 text-center" style={{ width: '80px' }}>VAT Amount</th>
              <th className="border border-gray-400 p-2 text-center" style={{ width: '80px' }}>Total Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-400 p-2 text-center">1</td>
              <td className="border border-gray-400 p-2">
                Monthly Fees for:<br />
                {extractProjectName(invoice.project_name)}<br />
                {invoice.type_of_service}<br />
                Month of <strong>{formatMonth(invoice.month)}</strong>
              </td>
              <td className="border border-gray-400 p-2 text-center font-bold">1</td>
              <td className="border border-gray-400 p-2 text-right font-bold" style={{ paddingRight: '8px' }}>
                {formatCurrency(invoice.unit_price)}
              </td>
              <td className="border border-gray-400 p-2 text-right font-bold" style={{ paddingRight: '8px' }}>
                {formatCurrency(invoice.taxable)}
              </td>
              <td className="border border-gray-400 p-2 text-center font-bold">5%</td>
              <td className="border border-gray-400 p-2 text-right font-bold" style={{ paddingRight: '8px' }}>
                {formatCurrency(invoice.vatAmount)}
              </td>
              <td className="border border-gray-400 p-2 text-right font-bold" style={{ paddingRight: '8px' }}>
                {formatCurrency(invoice.totalAmount)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Total Amount Row */}
        <table className="w-full border-collapse" style={{ borderColor: '#000' }}>
          <tbody>
            <tr className="font-bold">
              <td colSpan={4} className="border border-gray-400 p-2 text-center">
                Total Amount Due for Payment Including VAT (In AED)
              </td>
              <td className="border border-gray-400 p-2 text-right font-bold" style={{ width: '80px', paddingRight: '8px' }}>
                {formatCurrency(invoice.taxable)}
              </td>
              <td className="border border-gray-400 p-2" style={{ width: '60px' }}></td>
              <td className="border border-gray-400 p-2 text-right font-bold" style={{ width: '80px', paddingRight: '8px' }}>
                {formatCurrency(invoice.vatAmount)}
              </td>
              <td className="border border-gray-400 p-2 text-right font-bold" style={{ width: '80px', paddingRight: '8px' }}>
                {formatCurrency(invoice.totalAmount)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Amount in Words */}
        <table className="w-full border-collapse" style={{ borderColor: '#000' }}>
  <tbody>
    <tr>
      <td className="border border-gray-400 p-2 text-center font-bold">
        AED: {NumberToWords.convert(invoice.totalAmount)}
      </td>
    </tr>
  </tbody>
</table>

      </div>

      {/* Bank Details */}
      <div className="mt-4">
        <div className="font-bold" style={{ 
          borderBottom: '1px solid #000', 
          display: 'inline-block',
          paddingBottom: '2px',
          marginBottom: '8px'
        }}>
          Bank Details
        </div>
        <div className="grid grid-cols-[100px,1fr] gap-x-1 gap-y-0.5">
          <div>Account Name</div>
          <div className="font-bold">: Phoenician Technical Services LLC</div>
          <div>Bank Name & Branch</div>
          <div className="font-bold">: ABU DHABI COMMERCIAL BANK PJSC, Dubai, U.A.E</div>
          <div>Account Number</div>
          <div className="font-bold">: 10412895920001</div>
          <div>IBAN</div>
          <div className="font-bold">: AE460030010412895920001</div>
          <div>SWIFT</div>
          <div className="font-bold">: ADCBAEAA</div>
        </div>
      </div>
    </div>
  );
};
// Main Invoice Component
const Invoice = () => {
  // State Management
  const [success, setSuccess] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [generateDate, setGenerateDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [uniqueMonths, setUniqueMonths] = useState([]);
  const [uniqueServices, setUniqueServices] = useState([]);
  const [previewMode, setPreviewMode] = useState('single');
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const itemsPerPage = 10;
  const API_BASE_URL = 'https://accountss-3.onrender.com/api/accounts2';

  // Fetch Data
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/agreements`);
        const data = await response.json();
        
        if (data.success) {
          processInvoiceData(data.data);
        } else {
          throw new Error('Failed to fetch invoice data');
        }
      } catch (err) {
        console.error('Error fetching invoices:', err);
        setError('Failed to load invoices. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  // Process invoice data and extract unique values
  const processInvoiceData = (data) => {
    let allInvoices = [];
    const months = new Set();
    const services = new Set();

    data.forEach(agreement => {
      if (agreement.invoices && Array.isArray(agreement.invoices)) {
        const processedInvoices = agreement.invoices.map(invoice => {
          if (invoice.month) {
            months.add(formatDate(invoice.month));
          }
          if (agreement.type_of_service) {
            services.add(agreement.type_of_service);
          }

          return {
            ...invoice,
            ...agreement
          };
        });

        allInvoices = [...allInvoices, ...processedInvoices];
      }
    });

    setInvoices(allInvoices);
    setUniqueMonths(Array.from(months).sort().reverse());
    setUniqueServices(Array.from(services).sort());
  };

  // Filter invoices based on search and filters
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const matchesSearch = Object.values(invoice).some(value => 
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesMonth = selectedMonth === 'all' || 
        formatDate(invoice.month) === selectedMonth;
      const matchesService = selectedService === 'all' || 
        invoice.type_of_service === selectedService;
      
      return matchesSearch && matchesMonth && matchesService;
    });
  }, [invoices, searchTerm, selectedMonth, selectedService]);

  // Get current page's invoices
  const paginatedInvoices = useMemo(() => {
    return filteredInvoices.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  }, [filteredInvoices, page]);

  // Updated handleGenerateInvoice function
const handleGenerateInvoice = async (invoices) => {
  try {
    setError(null);
    setGeneratingPDF(true);
    
    if (Array.isArray(invoices) && invoices.length > 0) {
      const processedInvoices = invoices.map(invoice => {
        const unitPrice = parseFloat(invoice.unit_price || 0);
        const taxable = unitPrice;
        const vatAmount = parseFloat((taxable * 0.05).toFixed(2));
        const totalAmount = parseFloat((taxable + vatAmount).toFixed(2));
        
        return {
          ...invoice,
          invoiceDate: generateDate,
          unit_price: unitPrice,
          taxable: taxable,
          vatAmount: vatAmount,
          totalAmount: totalAmount,
          amountInWords: getAmountInWords(totalAmount)
        };
      });

      setSelectedInvoices(processedInvoices);
      setPreviewMode('multiple');
      setCurrentPreviewIndex(0);
    } else if (invoices) {
      const unitPrice = parseFloat(invoices.unit_price || 0);
      const taxable = unitPrice;
      const vatAmount = parseFloat((taxable * 0.05).toFixed(2));
      const totalAmount = parseFloat((taxable + vatAmount).toFixed(2));

      setSelectedInvoice({
        ...invoices,
        invoiceDate: generateDate,
        unit_price: unitPrice,
        taxable: taxable,
        vatAmount: vatAmount,
        totalAmount: totalAmount,
        amountInWords: getAmountInWords(totalAmount)
      });
      setPreviewMode('single');
    }
    setShowPreview(true);
  } catch (err) {
    console.error('Error generating invoice:', err);
    setError('Failed to generate invoice. Please try again.');
  } finally {
    setGeneratingPDF(false);
  }
};

const handleMultiplePDFDownload = async () => {
  try {
    setError(null);
    setGeneratingPDF(true);
    const invoicesToGenerate = previewMode === 'multiple' ? selectedInvoices : [selectedInvoice];
    
    for (let i = 0; i < invoicesToGenerate.length; i++) {
      const invoice = invoicesToGenerate[i];
      setCurrentPreviewIndex(i);
      
      // Wait for DOM update
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate PDF
      const element = document.getElementById(`invoice-preview-${invoice.invoice_no}`);
      if (!element) throw new Error('Invoice preview element not found');

      const pdfOptions = {
        margin: 0,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: {
          scale: 4,
          useCORS: true,
          letterRendering: true,
          backgroundColor: 'white'
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      // Generate PDF blob
      const pdfBlob = await html2pdf().set(pdfOptions).from(element).output('blob');
      
      try {
        // Upload to Supabase directly using the uploadInvoicePDF function
        const { filePath, publicUrl } = await uploadInvoicePDF(pdfBlob, invoice.invoice_no);
        
        // In handleMultiplePDFDownload function
const updateResponse = await fetch(`${API_BASE_URL}/update-invoice-storage`, {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
    },
    body: JSON.stringify({
        invoiceNo: invoice.invoice_no,
        storagePath: filePath,
        publicUrl: publicUrl
    })
});

if (!updateResponse.ok) {
    const errorData = await updateResponse.json();
    throw new Error(errorData.message || 'Failed to update storage information in database');
}

const responseData = await updateResponse.json();
console.log('Storage update response:', responseData);
        
        // Download for user
        const downloadUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `LOAMS-${invoice.invoice_no}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`Failed to upload invoice ${invoice.invoice_no}: ${uploadError.message}`);
      }

      if (i < invoicesToGenerate.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setSuccess('Invoice(s) generated and uploaded successfully');
    
  } catch (err) {
    console.error('Error details:', err);
    setError(err.message || 'Failed to generate PDFs');
  } finally {
    setGeneratingPDF(false);
  }
};
  return (
    <div className="service-agreement-container">
      {/* Header Controls */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Invoice Management</h2>
        <div className="flex gap-4 items-center">
          <Input
            type="date"
            value={generateDate}
            onChange={(e) => setGenerateDate(e.target.value)}
            className="w-40"
          />
          
          <Select value={selectedService} onValueChange={setSelectedService}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by Service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              {uniqueServices.map(service => (
                <SelectItem key={service} value={service}>{service}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[200px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {uniqueMonths.map(month => (
                <SelectItem key={month} value={month}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search invoices..."
              className="pl-8 w-[300px]"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <Button 
            onClick={() => handleGenerateInvoice(selectedInvoices)}
            disabled={selectedInvoices.length === 0}
          >
            <FileDown className="h-4 w-4 mr-2" />
            Generate Selected ({selectedInvoices.length})
          </Button>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-gray-500 text-sm mb-1">Total Invoices</h3>
          <p className="text-3xl font-bold">{filteredInvoices.length}</p>
        </div>
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-green-600 text-sm mb-1">Paid Invoices</h3>
          <p className="text-3xl font-bold text-green-600">
            {filteredInvoices.filter(inv => inv.amount_paid > 0).length}
          </p>
        </div>
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-yellow-600 text-sm mb-1">Pending Invoices</h3>
          <p className="text-3xl font-bold text-yellow-600">
            {filteredInvoices.filter(inv => !inv.amount_paid).length}
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {/* Add this near your error Alert */}
{success && (
  <Alert variant="success" className="mb-4">
    <AlertDescription>{success}</AlertDescription>
  </Alert>
)}

      {/* Main Table */}
<div className="bg-white rounded-lg shadow">
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="w-[50px]">
          <input
            type="checkbox"
            checked={selectedInvoices.length === paginatedInvoices.length}
            onChange={() => {
              setSelectedInvoices(prev => 
                prev.length === paginatedInvoices.length ? [] : paginatedInvoices
              );
            }}
            className="rounded border-gray-300"
          />
        </TableHead>
        <TableHead>Invoice No</TableHead>
        <TableHead>Tax Invoice Ref.</TableHead>
        <TableHead>Agreement No</TableHead>
        <TableHead>Project Name</TableHead>
        <TableHead>Type of Service</TableHead>
        <TableHead>Location</TableHead>
        <TableHead>Month</TableHead>
        <TableHead>Unit Price</TableHead>
        <TableHead>Status</TableHead>
        <TableHead className="w-[200px]">Actions</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {loading ? (
        <TableRow>
          <TableCell colSpan={11} className="text-center py-8">
            Loading invoices...
          </TableCell>
        </TableRow>
      ) : paginatedInvoices.length === 0 ? (
        <TableRow>
          <TableCell colSpan={11} className="text-center py-8">
            No invoices found
          </TableCell>
        </TableRow>
      ) : (
        paginatedInvoices.map((invoice) => (
          <TableRow key={invoice.invoice_no}>
            <TableCell>
              <input
                type="checkbox"
                checked={selectedInvoices.some(inv => inv.invoice_no === invoice.invoice_no)}
                onChange={() => {
                  setSelectedInvoices(prev => {
                    const isSelected = prev.some(inv => inv.invoice_no === invoice.invoice_no);
                    return isSelected 
                      ? prev.filter(inv => inv.invoice_no !== invoice.invoice_no)
                      : [...prev, invoice];
                  });
                }}
                className="rounded border-gray-300"
              />
            </TableCell>
            <TableCell>{invoice.invoice_no}</TableCell>
            <TableCell>LOAMS-{invoice.po}-{invoice.location}</TableCell>
            <TableCell>{invoice.po}</TableCell>
            <TableCell>{invoice.project_name}</TableCell>
            <TableCell>{invoice.type_of_service}</TableCell>
            <TableCell>{invoice.location}</TableCell>
            <TableCell>{formatDate(invoice.month)}</TableCell>
            <TableCell>{formatCurrency(invoice.unit_price)}</TableCell>
            <TableCell>
              <span className={`status-badge ${invoice.amount_paid > 0 ? 'paid' : 'unpaid'}`}>
                {invoice.amount_paid > 0 ? 'Paid' : 'Unpaid'}
              </span>
            </TableCell>
            <TableCell>
              <InvoicePreviewActions 
                invoice={invoice} 
                onDownload={() => handleGenerateInvoice(invoice)} 
              />
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
</div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-gray-600">
          Showing {paginatedInvoices.length ? ((page - 1) * itemsPerPage) + 1 : 0} to{' '}
          {Math.min(page * itemsPerPage, filteredInvoices.length)} of{' '}
          {filteredInvoices.length} entries
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          <span className="px-4 py-2">
            Page {page} of {Math.max(1, Math.ceil(filteredInvoices.length / itemsPerPage))}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(filteredInvoices.length / itemsPerPage)}
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[95vw] h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              <span>Invoice Preview</span>
              {previewMode === 'multiple' && (
                <span className="text-sm text-gray-600">
                  Invoice {currentPreviewIndex + 1} of {selectedInvoices.length}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
            {previewMode === 'single' && selectedInvoice && (
              <InvoicePreview invoice={selectedInvoice} />
            )}
            
            {previewMode === 'multiple' && selectedInvoices[currentPreviewIndex] && (
              <InvoicePreview 
                invoice={selectedInvoices[currentPreviewIndex]} 
                index={currentPreviewIndex}
              />
            )}
          </div>

          <DialogFooter className="border-t p-4 bg-white">
            <div className="flex justify-between w-full items-center">
              {previewMode === 'multiple' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPreviewIndex(i => Math.max(0, i - 1))}
                    disabled={currentPreviewIndex === 0}
                  >
                    Previous Invoice
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPreviewIndex(i => Math.min(selectedInvoices.length - 1, i + 1))}
                    disabled={currentPreviewIndex === selectedInvoices.length - 1}
                  >
                    Next Invoice
                  </Button>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Close
                </Button>
                <Button 
                  onClick={handleMultiplePDFDownload}
                  className="ml-2"
                  disabled={generatingPDF}
                >
                  {generatingPDF ? (
                    <>
                      <div className="animate-spin mr-2">⌛</div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileDown className="h-4 w-4 mr-2" />
                      Download {previewMode === 'multiple' ? 'All' : ''} PDF
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoice;
